import type {
  AgentConfig,
  AgentLanguage,
  AgentStatus,
  AgentUpdate,
  AnswerLength,
  CitationStyle,
  DayHours,
  PiiKind,
  RetrievalConfigIn,
  Tone,
} from "../shared/types";

/** "Route handoffs to": a team queue or one person (mirrors `HandoffRouteTo` on the server). */
export const HANDOFF_ROUTES = ["team", "person"] as const;
export type HandoffRouteTo = (typeof HANDOFF_ROUTES)[number];

export const SETTINGS_TABS = ["general", "instructions", "model", "retrieval", "knowledge", "guardrails", "escalation", "languages", "glossary", "memory", "danger"] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];
export const TONES: readonly Tone[] = ["warm", "professional", "cheerful", "respectful", "formal", "encouraging"];
export const LANGUAGES: readonly AgentLanguage[] = ["en", "fil", "taglish", "pam"];
export const PII_KINDS: readonly PiiKind[] = ["mobile", "email", "national_id", "address", "card_bank", "gov_id"];
export const WEEKDAYS: readonly DayHours["day"][] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const RETENTION_OPTIONS = ["30", "90", "180", "365"] as const;
export const CHUNK_SIZES = [256, 512, 1024] as const;
export const AVATAR_PRESETS = ["bot", "initials", "sparkles", "building", "headset"] as const;
export const POLICY_KEYS = ["no_guarantees", "no_medical_diagnosis", "no_legal_advice", "no_financial_advice"] as const;
export type PolicyKey = (typeof POLICY_KEYS)[number];
export const LIMITS = {
  nameMax: 80, descriptionMax: 500, instructionsMax: 8000, fallbackMax: 500, tonesMax: 3,
  maxTokensMin: 100, maxTokensMax: 4000, maxTokensStep: 50, confidenceMin: 0.2, confidenceMax: 0.9,
  overlapMax: 0.3, kMin: 20, kMax: 100, finalMin: 3, finalMax: 10, rerankMax: 0.8, perSourceMax: 10,
  strikesMin: 1, strikesMax: 5, weightTotal: 2, reminderMin: 1, reminderMax: 240, reminderDefault: 10, helplineMax: 500,
} as const;
/** ≈ words per token for the max-length hint. */
export const WORDS_PER_TOKEN = 0.75;
const PERCENT = 100;
const DEFAULT_OPEN = "08:00";
const DEFAULT_CLOSE = "17:00";
const WORKWEEK = new Set(["mon", "tue", "wed", "thu", "fri"]);

export interface RetrievalValues {
  chunk_target_tokens: number;
  chunk_overlap_ratio: number;
  k_fused: number;
  k_dense: number;
  k_lexical: number;
  final_top_n: number;
  /** 0 = keyword only … 100 = meaning only (w_dense share of w_dense + w_lexical). */
  hybrid: number;
  rerank_enabled: boolean;
  rerank_min_score: number;
  max_chunks_per_source: number;
  query_rewrite: boolean;
  contextual_retrieval: boolean;
  multi_query: boolean;
  neighbor_expansion: boolean;
  /** Knowledge graph (Experiment): `assist` on the server, off by default. */
  graph_retrieval: boolean;
}

export interface SettingsValues {
  name: string;
  description: string;
  avatar: string;
  status: AgentStatus;
  audiences: number[];
  instructions: string;
  tones: Tone[];
  answer_length: AnswerLength;
  model_id: string;
  temperature: number;
  max_tokens: number;
  strict: boolean;
  citation_style: CitationStyle;
  idk_enabled: boolean;
  confidence_threshold: number;
  fallback_message: string;
  followups: boolean;
  retrieval: RetrievalValues;
  blocked_topics: string[];
  pii: Record<PiiKind, boolean>;
  prompt_injection_guard: boolean;
  profanity_filter: boolean;
  policies: Record<PolicyKey, boolean>;
  custom_rules: { text: string }[];
  on_request_human: boolean;
  low_confidence_strikes: number;
  payment_topics: boolean;
  negative_sentiment: boolean;
  keywords: string;
  route_to: HandoffRouteTo;
  default_team_id: string;
  default_assignee_user_id: string;
  payment_team_id: string;
  match_team_topics: boolean;
  collect_contact: boolean;
  /** Minutes without a staff reply before the team is reminded (the Owner and Admins at twice that). */
  reminder_minutes: number;
  /** Required handovers (HND-001): the team that takes them, the helpline customers read, the member alerted out of hours, and the team a top-priority tier goes to (HND-002). */
  mandatory_team_id: string;
  helpline_text: string;
  on_call_user_id: string;
  priority_team_id: string;
  days: DayHours[];
  out_of_hours_message: string;
  languages: AgentLanguage[];
  reply_in_user_language: boolean;
  retention: string;
  remember_users: boolean;
  anonymize_after_retention: boolean;
}

type Bag = Record<string, unknown>;

function num(bag: Bag, key: string, fallback: number): number {
  const value = bag[key];
  return typeof value === "number" ? value : fallback;
}

function bool(bag: Bag, key: string, fallback: boolean): boolean {
  const value = bag[key];
  return typeof value === "boolean" ? value : fallback;
}

function str(bag: Bag, key: string, fallback = ""): string {
  const value = bag[key];
  return typeof value === "string" ? value : fallback;
}

function strings(bag: Bag, key: string): string[] {
  const value = bag[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

export function hybridFromWeights(dense: number, lexical: number): number {
  const total = dense + lexical;
  return total > 0 ? Math.round((dense / total) * PERCENT) : PERCENT / 2;
}

export function weightsFromHybrid(hybrid: number): { w_dense: number; w_lexical: number } {
  const dense = Math.round(((LIMITS.weightTotal * hybrid) / PERCENT) * PERCENT) / PERCENT;
  return { w_dense: dense, w_lexical: Math.round((LIMITS.weightTotal - dense) * PERCENT) / PERCENT };
}

export function defaultDays(): DayHours[] {
  return WEEKDAYS.map((day) => ({ day, open: WORKWEEK.has(day), opens_at: DEFAULT_OPEN, closes_at: DEFAULT_CLOSE }));
}

function routeFrom(escalation: Bag): HandoffRouteTo {
  return escalation.route_to === "person" ? "person" : "team";
}

function daysFrom(escalation: Bag): DayHours[] {
  const hours = escalation.office_hours;
  const raw = typeof hours === "object" && hours !== null ? (hours as Bag).days : undefined;
  const stored = Array.isArray(raw) ? (raw as Partial<DayHours>[]) : [];
  return defaultDays().map((fallback) => {
    const found = stored.find((d) => d.day === fallback.day);
    return found ? { day: fallback.day, open: found.open === true, opens_at: found.opens_at ?? DEFAULT_OPEN, closes_at: found.closes_at ?? DEFAULT_CLOSE } : { ...fallback, open: stored.length === 0 ? fallback.open : false };
  });
}

function retrievalFrom(config: Bag): RetrievalValues {
  return {
    chunk_target_tokens: num(config, "chunk_target_tokens", 512),
    chunk_overlap_ratio: num(config, "chunk_overlap_ratio", 0.15),
    k_fused: num(config, "k_fused", 40),
    k_dense: num(config, "k_dense", 50),
    k_lexical: num(config, "k_lexical", 50),
    final_top_n: num(config, "final_top_n", 6),
    hybrid: hybridFromWeights(num(config, "w_dense", 1), num(config, "w_lexical", 1)),
    rerank_enabled: bool(config, "rerank_enabled", true),
    rerank_min_score: num(config, "rerank_min_score", 0.3),
    max_chunks_per_source: num(config, "max_chunks_per_source", 3),
    query_rewrite: bool(config, "query_rewrite", true),
    contextual_retrieval: bool(config, "contextual_retrieval", false),
    multi_query: bool(config, "multi_query", false),
    neighbor_expansion: bool(config, "neighbor_expansion", false),
    graph_retrieval: str(config, "graph_retrieval", "off") === "assist",
  };
}

/** Agent config → form values (effective retrieval values, behaviour flags, escalation table; the model the agent really answers with, which differs from the saved one while its provider is off). */
export function configToForm(config: AgentConfig): SettingsValues {
  const behavior = config.behavior;
  const guard = config.guardrails;
  const esc = config.escalation;
  const pii = (typeof guard.pii_redaction === "object" && guard.pii_redaction !== null ? guard.pii_redaction : {}) as Bag;
  const retrieval = config.retrieval_config;
  return {
    name: config.name, description: config.description ?? "", avatar: str(config.avatar, "preset", "bot"),
    status: config.status, audiences: [...config.audiences], instructions: config.instructions,
    tones: config.tones as Tone[], answer_length: config.answer_length as AnswerLength, model_id: config.effective_model?.id ?? config.model_id ?? "",
    temperature: config.temperature, max_tokens: config.max_tokens ?? 600, strict: config.grounding_mode === "strict",
    citation_style: config.citation_style as CitationStyle, idk_enabled: bool(behavior, "idk_enabled", true),
    confidence_threshold: num(retrieval, "confidence_threshold", 0.55), fallback_message: str(behavior, "fallback_message"),
    followups: bool(behavior, "followups", true), retrieval: retrievalFrom(retrieval),
    blocked_topics: strings(guard, "blocked_topics"),
    pii: Object.fromEntries(PII_KINDS.map((k) => [k, bool(pii, k, k === "mobile" || k === "email")])) as Record<PiiKind, boolean>,
    prompt_injection_guard: bool(guard, "prompt_injection_guard", true), profanity_filter: bool(guard, "profanity_filter", true),
    policies: Object.fromEntries(POLICY_KEYS.map((k) => [k, bool(guard, k, false)])) as Record<PolicyKey, boolean>,
    custom_rules: strings(guard, "custom_rules").map((text) => ({ text })),
    on_request_human: bool(esc, "on_request_human", true), low_confidence_strikes: num(esc, "low_confidence_strikes", 2),
    payment_topics: bool(esc, "payment_topics", false), negative_sentiment: bool(esc, "negative_sentiment", true),
    keywords: strings(esc, "keywords").join(", "), route_to: routeFrom(esc), default_team_id: str(esc, "default_team_id"),
    default_assignee_user_id: str(esc, "default_assignee_user_id"), payment_team_id: str(esc, "payment_team_id"),
    match_team_topics: bool(esc, "match_team_topics", true),
    collect_contact: bool(esc, "collect_contact", false), reminder_minutes: num(esc, "reminder_minutes", LIMITS.reminderDefault), days: daysFrom(esc), out_of_hours_message: str(esc, "out_of_hours_message"),
    mandatory_team_id: str(esc, "mandatory_team_id"), helpline_text: str(esc, "helpline_text"), on_call_user_id: str(esc, "on_call_user_id"), priority_team_id: str(esc, "priority_team_id"),
    languages: config.languages as AgentLanguage[], reply_in_user_language: config.reply_in_user_language,
    retention: config.retention_days === null ? "" : String(config.retention_days),
    remember_users: bool(behavior, "remember_users", true), anonymize_after_retention: bool(behavior, "anonymize_after_retention", false),
  };
}

export function parseKeywords(text: string): string[] {
  return [...new Set(text.split(",").map((k) => k.trim()).filter(Boolean))];
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function retrievalPatch(next: RetrievalValues, prev: RetrievalValues): RetrievalConfigIn | null {
  const patch: RetrievalConfigIn = {};
  const keys = ["chunk_target_tokens", "chunk_overlap_ratio", "k_fused", "k_dense", "k_lexical", "final_top_n", "rerank_enabled", "rerank_min_score", "max_chunks_per_source", "query_rewrite", "contextual_retrieval", "multi_query", "neighbor_expansion"] as const;
  for (const key of keys) if (next[key] !== prev[key]) Object.assign(patch, { [key]: next[key] });
  if (next.hybrid !== prev.hybrid) Object.assign(patch, weightsFromHybrid(next.hybrid));
  if (next.graph_retrieval !== prev.graph_retrieval) patch.graph_retrieval = next.graph_retrieval ? "assist" : "off";
  return Object.keys(patch).length > 0 ? patch : null;
}

function guardrails(v: SettingsValues) {
  return {
    blocked_topics: v.blocked_topics, pii_redaction: v.pii, prompt_injection_guard: v.prompt_injection_guard,
    profanity_filter: v.profanity_filter, ...v.policies, custom_rules: v.custom_rules.map((r) => r.text.trim()).filter(Boolean),
  };
}

function escalation(v: SettingsValues, timezone: string) {
  return {
    on_request_human: v.on_request_human, low_confidence_strikes: v.low_confidence_strikes, payment_topics: v.payment_topics,
    negative_sentiment: v.negative_sentiment, keywords: parseKeywords(v.keywords), route_to: v.route_to,
    default_team_id: v.default_team_id || null, default_assignee_user_id: v.default_assignee_user_id || null,
    payment_team_id: v.payment_team_id || null, match_team_topics: v.match_team_topics, collect_contact: v.collect_contact,
    reminder_minutes: v.reminder_minutes, office_hours: { timezone, days: v.days }, out_of_hours_message: v.out_of_hours_message.trim(),
    mandatory_team_id: v.mandatory_team_id || null, helpline_text: v.helpline_text.trim(), on_call_user_id: v.on_call_user_id || null, priority_team_id: v.priority_team_id || null,
  };
}

/** Form → `PATCH /v1/agents/{id}` body with only the sections that changed. */
export function formToUpdate(next: SettingsValues, prev: SettingsValues, timezone: string): AgentUpdate {
  const body: AgentUpdate = {};
  const plain = ["status", "instructions", "tones", "answer_length", "temperature", "max_tokens", "citation_style", "idk_enabled", "confidence_threshold", "followups", "languages", "reply_in_user_language"] as const;
  for (const key of plain) if (!same(next[key], prev[key])) Object.assign(body, { [key]: next[key] });
  if (next.name !== prev.name) body.name = next.name.trim();
  if (next.description !== prev.description) body.description = next.description.trim() || null;
  if (next.avatar !== prev.avatar) body.avatar = { preset: next.avatar };
  if (!same(next.audiences, prev.audiences)) body.audiences = [...next.audiences].sort((a, b) => a - b);
  if (next.model_id !== prev.model_id && next.model_id) body.model_id = next.model_id;
  if (next.strict !== prev.strict) body.grounding_mode = next.strict ? "strict" : "balanced";
  if (next.fallback_message !== prev.fallback_message) body.fallback_message = next.fallback_message.trim();
  const retrieval = retrievalPatch(next.retrieval, prev.retrieval);
  if (retrieval) body.retrieval_config = retrieval;
  if (!same(guardrails(next), guardrails(prev))) body.guardrails = guardrails(next);
  if (!same(escalation(next, timezone), escalation(prev, timezone))) body.escalation = escalation(next, timezone);
  if (next.retention !== prev.retention) body.retention_days = next.retention ? Number(next.retention) : null;
  if (next.remember_users !== prev.remember_users || next.anonymize_after_retention !== prev.anonymize_after_retention) {
    body.behavior = { remember_users: next.remember_users, anonymize_after_retention: next.anonymize_after_retention };
  }
  return body;
}

export function isTab(value: string | undefined): value is SettingsTab {
  return (SETTINGS_TABS as readonly string[]).includes(value ?? "");
}

export function approxWords(tokens: number): number {
  return Math.round(tokens * WORDS_PER_TOKEN);
}
